import { TimeDisplays, TimeFormats } from '@macs-frontend/core/utils/consts';
import { AppDateTime } from '@macs-frontend/core/utils/datetime';
import { isoUUID, UUID } from '@macs-frontend/core/utils/uuid';
import { PriceModes } from '@macs-frontend/features/company-management/financial/financial.types';
import { InvoiceItemTypes } from '@macs-frontend/features/shared/models/invoice-item.model';
import { User } from '@macs-frontend/web-macs/shared';
import { ContractTariffBase, Plan, QuantityType } from '@macs-gql';
import { AbstractLocation, Currency } from '..';
import { Contact } from './contact';
import { GeoCity, GeoCountry } from './geo';
import { Timezone } from './timezone';

export interface CompanyLegalLocation extends AbstractLocation {}

export enum CompanyHourlyCostSources {
  TASK = 'TASK',
  PERSON = 'PERSON',
}
// @ts-expect-error
export const emptyCompany: Company = Object.freeze({
  uuid: isoUUID.wrap('22a475df-c2a3-4f90-a766-2fba26339ee1'),
  name: '',
  status: '',
  logo: '',
  webAddress: '',
  alternativeName: '',
  brandColor: '',
  billingAddressFull: '',
  billingAddress: '',
  btwVat: '',
  iban: '',
  bic: '',
  RPRLocation: '',
  signature: '',
  zip: '',
  state: '',
  legalAddress: '',
  legalAddressFull: '',
  legalState: '',
  legalZip: '',
  useLegalAddressForBilling: false,
  currencyFormat: '',
  numberFormat: '1.234,56',
  fiscalYear: '',
  startWeek: 0,
  timerRounding: '',
  timeFormat: TimeFormats.HOURS_24,
  timeDisplay: TimeDisplays.DECIMAL,
  timerMode: '',
  dateFormat: '',
  createdAt: "2000-01-01'T'00:00:00" as AppDateTime,
  legalRemarksForEstimates: '',
  legalRemarksForInvoices: '',
  legalRemarksForSPOs: '',
  initials: '',
  financeId: '',
  exactOnline: false,
  bookkeepingApiUrl: '',
  bookkeepingUsername: '',
  bookkeepingPassword: '',
  plannerEmailEnabled: false,
  plannerEmailFrom: '',
  plannerMailerDsn: '',
  moreInfo: '',
  representative: '',
  userQty: 0,
  language: null,
  hourlyCostSource: CompanyHourlyCostSources.TASK,
  priceMode: PriceModes.Fee,
  mainAccount: {} as User,
  city: {} as GeoCity,
  country: {} as GeoCountry,
  legalCity: {} as GeoCity,
  legalCountry: {} as GeoCountry,
  legalLocation: {} as CompanyLegalLocation,
  users: [],
  currency: {} as Currency,
  timezone: {} as Timezone,
  contacts: [],
  defaultQuantityType: {} as QuantityType,
  plan: {} as Pick<Plan, 'date'>,
  emails: [
    {
      label: '',
      email: '',
    },
  ],
  phones: [
    {
      label: '',
      number: '',
    },
  ],
  cpoReceivedEnabled: false,
  timeTrackingRequiresEstimate: false,
  defaultInvoiceItemType: InvoiceItemTypes.Expense,
  canCreateClientWithoutVat: false,
  btRequiredForValidatedEstimates: false,
  spoExpensesFromEstimatesOnly: false,
  showMarginsOnEstimatePreview: true,
  includeEstimateInInvoiceRequestPdf: false,
  enableInvoiceTotalsAudit: false,
  useLegacyContractPdfTemplates: false,
  allowSingleItems: true,
  defaultReviewer: null,
  requiredMinimumMargin: 0,
  requiredMargin: 0,
  defaultAmbassadorCostPrice: null,
  nssoNumber: '',
  hotlineNumber: '',
  pointhrApiKey: '',
  pointhrUuid: '',
  pointhrPassKey: '',
  pointhrApiUrl: '',
  pointhrEnabled: false,
  useAlternativeProjectIdForContracts: false,
  messagingEnabled: false,
});

export interface BaseCompany {
  uuid: UUID;
  name: string;
  status: string;
  logo: string;
  webAddress: string;
  alternativeName: string;
  brandColor: string;
  billingAddressFull: string;
  billingAddress: string;
  btwVat: string;
  iban: string;
  bic: string;
  RPRLocation: string;
  signature: string;
  zip: string;
  state: string;
  legalAddress: string;
  legalAddressFull: string;
  legalState: string;
  legalZip: string;
  useLegalAddressForBilling?: boolean;
  currencyFormat: string;
  numberFormat: string;
  fiscalYear: string;
  startWeek: number;
  timerRounding: string;
  timeFormat: TimeFormats;
  timeDisplay: TimeDisplays;
  timerMode: string;
  dateFormat: string;
  createdAt?: AppDateTime;
  legalRemarksForEstimates: string;
  legalRemarksForInvoices: string;
  legalRemarksForSPOs: string;
  initials?: string;
  financeId?: string;
  exactOnline: boolean;
  bookkeepingApiUrl?: string;
  bookkeepingUsername?: string;
  bookkeepingPassword?: string;
  plannerEmailEnabled: boolean;
  plannerEmailFrom?: string | null;
  plannerMailerDsn?: string | null;
  moreInfo?: string;
  representative?: string;
  userQty?: number;
  language: string | null;
  hourlyCostSource: CompanyHourlyCostSources;
  priceMode: PriceModes;
  cpoReceivedEnabled: boolean;
  /** When true, tasks with no estimate link cannot be time tracked. */
  timeTrackingRequiresEstimate: boolean;
  isParent: boolean;
  contractTariffBase: ContractTariffBase;
}

export interface Company extends BaseCompany {
  mainAccount: User;
  city: GeoCity;
  country: GeoCountry;
  legalCity: GeoCity;
  legalCountry: GeoCountry;
  legalLocation?: CompanyLegalLocation;
  users: User[];
  currency: Currency;
  timezone: Timezone;
  contacts?: Contact[];
  plan?: Pick<Plan, 'date'>;
  emails: [
    {
      label?: string;
      email: string;
    },
  ];
  phones: [
    {
      label?: string;
      number: string;
    },
  ];
  defaultInvoiceItemType: InvoiceItemTypes;
  defaultTaxRate: { uuid: UUID; name: string } | null;
  canCreateClientWithoutVat: boolean;
  btRequiredForValidatedEstimates: boolean;
  spoExpensesFromEstimatesOnly: boolean;
  showMarginsOnEstimatePreview: boolean;
  includeEstimateInInvoiceRequestPdf: boolean;
  enableInvoiceTotalsAudit: boolean;
  useLegacyContractPdfTemplates: boolean;
  allowSingleItems: boolean;
  requiredMinimumMargin: number;
  requiredMargin: number;
  // Cents, not euros — mirrors ExternalUser.costPrice. null means no default is set.
  defaultAmbassadorCostPrice: number | null;
  defaultReviewer: User | null;
  nssoNumber: string | null;
  hotlineNumber: string | null;
  pointhrApiKey: string | null;
  pointhrUuid: string | null;
  pointhrPassKey: string | null;
  pointhrApiUrl: string | null;
  pointhrEnabled: boolean;
  useAlternativeProjectIdForContracts: boolean;
  messagingEnabled: boolean;
  plannerEmailEnabled: boolean;
  plannerEmailFrom: string | null;
  plannerMailerDsn: string | null;
}

export interface CompanyPublic
  extends Pick<
    Company,
    | 'uuid'
    | 'name'
    | 'legalCountry'
    | 'legalCity'
    | 'legalAddressFull'
    | 'currency'
    | 'numberFormat'
    | 'currencyFormat'
  > {}

export enum CompanyStatuses {
  ALL = 'ALL',
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  DRAFT = 'DRAFT',
}

export interface ContactPerson {
  uuid: UUID;
  username: string;
  contactPhotoFilename: string;
  firstName: string;
  lastName: string;
  phones: string[];
  email: string[];
}
